// Per-breed anaesthetic profile, co-located so adding a breed is a single edit.
// Each profile carries:
//   vulns    – complication keys (ISSUE_DEFS) the breed is prone to. The Monitor's
//              "Random complications" mode weights these 4× more likely.
//   baseline – small deltas added to SPECIES_BASE so each preset settles to slightly
//              different vitals before any complication fires. Only hr/rr/temp/map
//              persist in the sim (spo2/etco2 are recomputed each tick), so nudge those.
//              Kept small enough that the patient still starts within the normal range.
//   watch    – a short clinical "watch-points" reminder shown on the monitor header.
//
// Complication keys: apnoea · hypoxemia · hypothermia · bradycardia · tachycardia · hypertension · arrhythmia
// Lookup is by exact breed string (matches the Case Library presets + intake breed list).
export const BREED_PROFILES = {
  // ---- new breed-specific presets ----
  Greyhound: {
    vulns: ["hypothermia", "bradycardia"],
    baseline: { hr: -15, temp: -0.6, map: 6 },
    watch: "Sighthound — guard temperature; expect a prolonged recovery",
  },
  "Doberman Pinscher": {
    vulns: ["arrhythmia", "tachycardia"],
    baseline: { hr: 6, map: -6 },
    watch: "vWD / DCM — watch for VPCs & bleeding; support MAP",
  },
  "Cavalier King Charles Spaniel": {
    vulns: ["tachycardia", "arrhythmia"],
    baseline: { hr: 8, map: -10 },
    watch: "MVD — protect MAP, avoid tachycardia & fluid overload",
  },
  "Rough Collie": {
    vulns: ["bradycardia", "apnoea"],
    baseline: { hr: -6 },
    watch: "MDR1 — reduce sedatives; watch for an over-deep plane",
  },
  "Maine Coon": {
    vulns: ["arrhythmia", "tachycardia"],
    baseline: { hr: 6, map: -4 },
    watch: "HCM — keep HR low-normal, conservative fluids",
  },
  Chihuahua: {
    vulns: ["hypothermia", "bradycardia"],
    baseline: { hr: 12, temp: -0.6 },
    watch: "Toy breed — guard temperature & glucose; small ETT",
  },

  // ---- existing presets ----
  Boxer: {
    vulns: ["bradycardia", "arrhythmia"],
    baseline: { hr: -8 },
    watch: "Vagal bradycardia & murmur — watch HR and rhythm",
  },
  Persian: {
    vulns: ["hypoxemia", "apnoea"],
    baseline: { hr: 6, rr: 4 },
    watch: "Brachycephalic cat — protect the airway",
  },
  "Domestic Shorthair": {
    vulns: ["hypertension", "bradycardia"],
    baseline: { map: 8, temp: -0.3 },
    watch: "Geriatric CKD — avoid hypotension; renal-safe drugs",
  },
  "French Bulldog": {
    vulns: ["hypoxemia", "apnoea"],
    baseline: { rr: 4 },
    watch: "BOAS — high airway / regurgitation risk; pre-oxygenate",
  },
  "Golden Retriever": {
    vulns: ["hypothermia", "bradycardia"],
    baseline: { temp: -0.4 },
    watch: "Long procedure — guard temperature; plan a locoregional block",
  },
};

const profileFor = (patient) =>
  (patient && patient.breed && BREED_PROFILES[patient.breed]) || null;

// Complication keys this patient's breed is prone to, or [].
export function getBreedVulnerabilities(patient) {
  return profileFor(patient)?.vulns || [];
}

// Baseline vital deltas (hr/rr/temp/map) for this patient's breed, or {}.
export function getBreedBaseline(patient) {
  return profileFor(patient)?.baseline || {};
}

// Short clinical watch-points reminder for this patient's breed, or "".
export function getBreedWatch(patient) {
  return profileFor(patient)?.watch || "";
}
